import {useEffect, useReducer, useRef, useState} from "react";
import { useNavigate } from "react-router-dom";
import ButtonBot from "shared/components/ButtonBot";
import monthlyPayment from "assets/icons/monthlyPayment.svg";
import AppInfoRow from "shared/components/AppInfoRow";
import SubHeader from "../../../shared/components/SubHeader";
import SearchBar from "../../../shared/components/SearchBar";
import { useDispatch, useSelector } from "react-redux";
import { getMembership, onPaymentFee } from "../usecases/CarInOutUsecases";
import EmptySite from "shared/components/EmptySite";
import { datetimeFormat, getFormattedTime, maskCardNumber, numberFormat, makeCenterLine, makeFixedLine } from "../../../utils/common";
import { setAppLoading } from "../../../shared/store/headerSlice";
import Popup from "shared/components/Popup";
import cardIcon from "assets/icons/cardIcon.svg";
import { ThreeDots } from 'react-loader-spinner';
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import {onGetParkingTid} from "../utils/parkingUtils";


const initialPopupState = {
  showPopupPay: false,
  showPopupPayError: false,
  showPopupNice: false,
};

const popupReducer = (state, action) => {
  switch (action.type) {
    case 'SET_POPUP':
      return { ...state, [action.payload.key]: action.payload.value };
    case 'RESET':
      return initialPopupState;
    default:
      return state;
  }
};

const ParkingPaymentMonthly = () => {
  const { user, serverTime } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [popupState, dispatchPopup] = useReducer(popupReducer, initialPopupState);
  const [carNumber, setCarNumber] = useState("");
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [parkingTid, setParkingTid] = useState(null);
  const [payData, setPayData] = useState({});
  const [membership, setMembership] = useState({
    enablePayment: false,
    feeFinal: 0,
    operatingPeriodBegin: "",
    operatingPeriodEnd: "",
  });

  const membershipRef = useRef(null)
  useEffect(() => {
    membershipRef.current = membership
  }, [membership])

  const onSearchMembership = async () => {
    try {
      dispatch(setAppLoading(true));
      const response = await getMembership({
        parkingId: user?.parkingId,
        carNumber: carNumber
      });
      setMembership({
        ...response,
        carNumber: carNumber
      });
    } catch (error) {
      console.log(error)
    } finally {
      dispatch(setAppLoading(false));
    }

  }

  useEffect(() => {
    // window.addEventListener('message', responseHandler);
    document.addEventListener('message', handlePaymentResponse);
    return () => document.removeEventListener('message', handlePaymentResponse);
  }, [parkingTid]);

  const handlePaymentResponse = (event) => {
    const msg = JSON.parse(event.data);
    // alert(JSON.stringify(msg));
    try {
      if (msg.type === 'payRes') {
        if (['0000', '0021'].includes(msg.body.responseCode)) {
          setPayData(msg.body);
          onPaymentSuccess(msg.body);
        } else {
          setLoadingPayment(false);
          dispatchPopup({ type: 'SET_POPUP', payload: { key: 'showPopupPayError', value: true } });
        }
      } else if (msg.type === 'printRes') {
        if (['0000', '0021'].includes(msg.body.responseCode)) {
          // when print success
          toast.success('성공했습니다.', {
            position: "top-center",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            className: 'htToastSuccess',
          });
        }
      }else if (msg.type === 'notInstalled') {
        dispatchPopup({ type: 'SET_POPUP', payload: { key: 'showPopupNice', value: true } });
        setLoadingPayment(false);
      }
    } catch (e) {
      console.error('Fail to parse message:', e);
    }
  };

  const onPayment = async () => {

    try {

      dispatchPopup({ type: 'RESET' });
      setLoadingPayment(true);

      const payKey = await onGetParkingTid(user.parkingId);

      if (!payKey || !payKey.tid) {
        setLoadingPayment(false);
        dispatchPopup({ type: 'SET_POPUP', payload: { key: 'showPopupPayError', value: true } });
        return;
      } else {
        setParkingTid(payKey.tid);
      }

      // payment params
      const params = new URLSearchParams({
        billType: '0',                       // 전문종류 ( 0:신용 )
        terminalNo: payKey?.tid,             // 단말기 번호
        orderKey: '99999',                   // 주문번호 ( 요청/응답 명시 용도 )
        authType: '0',                       // 0: 승인, 1: 취소
        installment: '00',                   // 할부 개월 ( 00:일시불 )
        amount: membership?.feeFinal || '0', // 거래금액 ( 부가세, 봉사료 제외 금액 )
        tax: '0',                            // 세금 (부가세 없음)
        tip: '0',                            // 봉사료(없음)
        isSign: 'D',                         // 서명처리 ( ‘Y’:사용, ‘N’:미사용, ‘D’: NICEPAY 앱 판단 )
        resScheme: 'innodep://payResponse',  // 응답 스키마
      });

      if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
        window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: "payReq", body: params.toString() })
        );
      } else {
        setLoadingPayment(false);
        console.error('Cannot find Native App !!');
      }

      // pay request End
    } catch (error) {
      console.error("Error during payment:", error);
      dispatchPopup({ type: 'SET_POPUP', payload: { key: 'showPopupPayError', value: true } });
    }

  }

  const onPaymentSuccess = async (payRes) => {
    try {
      dispatchPopup({ type: 'RESET' });
      setLoadingPayment(true);

      const data = {
        parkingId: user.parkingId,
        facilityId: "MOBILE",
        carNumber: membershipRef.current?.carNumber,
        userId: user.userId,
      }

      const payTime = DateTime.fromFormat(`20${payRes?.authDate}`, "yyMMddHHmmss");
      const price = membershipRef.current?.feeFinal || 0;
      const payment = await onPaymentFee({
        ...data,
        payWay: price == 0 ? "CASH" : "CRDCRD",
        payStationId: user.userId,
        price: price,
        payTime: payTime?.isValid ? payTime.toISO() : getFormattedTime(serverTime, "ISO"),
        entryList: [
          {
            payType: "REGISTRATION_FEE",
            parkingEntryId: "",
            membershipApplicantId: membershipRef.current?.membershipApplicantId,   // 정기권ID
            extendedMonths: 1,           // 정기권 연장 달수
          }
        ],

        cardNumber: maskCardNumber(payRes?.track2Data),
        approvalNumber: payRes?.authNo,
        buyerCode: payRes?.buyerCode,
        buyerName: payRes?.buyerCompany,
        issuerCode: payRes?.issuerCode,
        issuerName: payRes?.issuerCompany,
        storeNumber: payRes?.storeNo,
        terminalId: parkingTid,
        cardReadData: payRes?.payUniqueNo,

      });

      const response = await getMembership({
        parkingId: user?.parkingId,
        carNumber: membership?.carNumber || carNumber
      });
      setMembership({
        ...response,
        carNumber: carNumber
      });
      setLoadingPayment(false);

    } catch (error) {
      console.log(error)
    }

    onBillPrint(payRes);
  }

  const onBillPrint = async (payRes) => {

    let title = '매출전표';

    const encoder = new TextEncoder(); // 기본 utf-8

    let form = [
      0x0A,
      0x1B, 0x40, //명령 초기화

      0x1D, 0x21, 0x11,   // 크기 크게
      ...encoder.encode(makeCenterLine(title, true)), 0x0A,
      0x1D, 0x21, 0x00,   // 크기 초기화

      0x1B, 0x61, 0x02,   // 우측 정렬
      ...encoder.encode('No:' + (window.APP_CONFIG?.parkingSeq || '000010')), 0x0A,
      0x1B, 0x61, 0x00,   // 왼쪽 정렬

      0x1D, 0x21, 0x11,   // 크기 크게
      ...encoder.encode('-'.repeat(24)), 0x0A,
      0x1D, 0x21, 0x00,   // 크기 초기화
      0x0A,

      ...encoder.encode(makeFixedLine('주차장명:', user?.parking?.parkingName)), 0x0A,
      ...encoder.encode(makeFixedLine('사업자주소:', user?.parking?.address)), 0x0A,
      ...encoder.encode(makeFixedLine('전화번호:', user?.parking?.phoneNumber)), 0x0A,
      ...encoder.encode(makeFixedLine('일시:', getFormattedTime(serverTime, "yyyy-MM-dd HH:mm:ss"))), 0x0A,

      0x1D, 0x21, 0x11,   // 크기 크게
      ...encoder.encode('-'.repeat(24)), 0x0A,
      0x1D, 0x21, 0x00,   // 크기 초기화
      0x0A,

      ...encoder.encode(makeFixedLine('차량번호:', membershipRef.current?.carNumber || carNumber)), 0x0A,
      ...encoder.encode(makeFixedLine('정기권시작:', datetimeFormat(membershipRef.current?.operatingPeriodBegin, "yyyyMMddHHmmss", "yyyy-MM-dd HH:mm"))), 0x0A,
      ...encoder.encode(makeFixedLine('정기권종료:', datetimeFormat(membershipRef.current?.operatingPeriodEnd, "yyyyMMddHHmmss", "yyyy-MM-dd HH:mm"))), 0x0A,
      // ...encoder.encode(makeFixedLine('할인금액:', '-' + numberFormat(checkOutData?.fee?.feeDiscount))), 0x0A,
      ...encoder.encode(makeFixedLine('정산금액:', numberFormat(membershipRef.current?.feeFinal))), 0x0A,

      0x1D, 0x21, 0x11,   // 크기 크게
      ...encoder.encode('-'.repeat(24)), 0x0A,
      0x1D, 0x21, 0x00,   // 크기 초기화
      0x0A,

      ...encoder.encode(makeFixedLine('카드번호:', maskCardNumber(payRes?.track2Data))), 0x0A,
      ...encoder.encode(makeFixedLine('승인번호:', payRes?.authNo)), 0x0A,
      ...encoder.encode(makeFixedLine('카드사명:', payRes?.issuerCompany)), 0x0A,
      ...encoder.encode(makeFixedLine('매입사명:', payRes?.buyerCompany)), 0x0A,

      0x1D, 0x21, 0x11,   // 크기 크게
      ...encoder.encode('-'.repeat(24)), 0x0A,
      0x1D, 0x21, 0x00,   // 크기 초기화
      0x0A,

      ...encoder.encode(makeCenterLine('이용해 주셔서 감사합니다.')), 0x0A

    ];

    const formData = new Uint8Array(form);

    const formEnc = btoa(String.fromCharCode(...formData));

    const payKey = await onGetParkingTid(user.parkingId);

    if (!payKey || !payKey.tid) {
      dispatchPopup({ type: 'SET_POPUP', payload: { key: 'showPopupPayError', value: true } });
      return;
    }

    // print params for VAN API
    const params = new URLSearchParams({
      billType: '9',                        // 전문종류 ( 9:프린터출력 )
      terminalNo: payKey.tid,               // 단말기 번호
      orderKey: '99999',                    // 주문번호 ( 요청/응답 명시 용도 )
      printResType: '1',
      resScheme: 'innodep://printResponse', // 응답 스키마
    });

    if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
      window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "print", body: params.toString(), printForm: formEnc })
      );
    } else {
      // setLoadingPayment(false);
      console.error('Cannot find Native App !!');
    }
  }

  return (
    <>
      {loadingPayment ?
        <div className="htContainer htPaymentSuccess">
          <img src={cardIcon} alt="Card" />
          <div className="htPsTile">결제중입니다</div>
          <div className="htPsDesc">나이스페이먼츠 결제 모듈 영역</div>
          <ThreeDots
            height="60"
            width="60"
            color="#4fa94d"
            ariaLabel="loading"
          />
        </div>
        : <div className="htFixed">
          <SubHeader >
            <div className={`sub-header-text w-full justify-between`} >
              <div className="sub-header-text w-full" style={{ fontSize: '15px' }}>
                <span className="htColorGray2">주차장명 : </span>
                <span className="htBold">{user?.parking?.parkingName} ({user?.parkingId})</span>
              </div>
            </div>
          </SubHeader>

          <SearchBar
            value={carNumber}
            onChange={(e) => {
              setCarNumber(e.target.value);
            }}
            handleSearchClick={() => {
              onSearchMembership();
            }}
            onClear={() => {
              setCarNumber("");

            }}
          />
          <ButtonBot textYes={`결제`} handleYes={() => { dispatchPopup({ type: 'SET_POPUP', payload: { key: 'showPopupPay', value: true } }); }}
          // icon={monthlyPayment}
          disable={!membership?.enablePayment} />

          <div className="nl-parking-payment-monthly">
            <div className="nl-content">
              {membership?.membershipApplicantId ? <>
                <AppInfoRow label="차량번호" value={`${membership?.carNumber || ""}`} valueClass="" />
                <AppInfoRow label="월정기 시작일" value={`${datetimeFormat(membership?.operatingPeriodBegin, "yyyyMMddHHmmss", "yyyy-MM-dd HH:mm") || ""}`} valueClass="" />
                <AppInfoRow label="월정기 종료일" value={`${datetimeFormat(membership?.operatingPeriodEnd, "yyyyMMddHHmmss", "yyyy-MM-dd HH:mm") || ""}`} valueClass="" />
                <AppInfoRow label="상태" value={`${membership?.enablePayment == true ? "연장 미결제" : "연장 결제 완료"}`} valueClass="" />
                <div className="htInfoRow htBold">
                  <span className="">결제금액</span>
                  <span className="htTotal">{membership?.feeFinal ? `${numberFormat(membership?.feeFinal)}원` : ""} </span>
                </div>
              </> : <EmptySite />}

            </div>

          </div>
          {popupState.showPopupPay && (
            <Popup
              title="결제 확인"
              handleYes={onPayment}
              textYes="예"
              textNo="아니오"
              handleNo={() => {
                dispatchPopup({ type: 'SET_POPUP', payload: { key: 'showPopupPay', value: false } });
              }}
              setVisible={(value) => dispatchPopup({ type: 'SET_POPUP', payload: { key: 'showPopupPay', value: value } })}
            >
              <div className="nl-row f-column">
                <div>결제를 진행하시겠습니까?</div>
              </div>
            </Popup>
          )}
          {popupState.showPopupPayError && (
              <Popup
                  title="결제"
                  handleYes={() => dispatchPopup({ type: 'SET_POPUP', payload: { key: 'showPopupPayError', value: false } })}
                  textYes="예"
                  setVisible={(value) => dispatchPopup({ type: 'SET_POPUP', payload: { key: 'showPopupPayError', value: value } })}
              >
                <div className="nl-row f-column">
                  <div>결제 중 오류가 발생했습니다. 다시 시도해 주세요!</div>
                </div>
              </Popup>
          )}
          {popupState.showPopupNice && (
              <Popup
                  title="결제"
                  handleYes={() => dispatchPopup({ type: 'SET_POPUP', payload: { key: 'showPopupNice', value: false } })}
                  textYes="예"
                  setVisible={(value) => dispatchPopup({ type: 'SET_POPUP', payload: { key: 'showPopupNice', value: value } })}
              >
                <div className="nl-row f-column">
                  <div>NICEPAY 앱을 설치하고 다시 시도해 주세요!</div>
                </div>

              </Popup>
          )}
        </div>}

    </>

  );
};

export default ParkingPaymentMonthly;
